import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AumicoAnnualReportService } from '@modeso/aumico-lib-annualreport-fe';
import {
    IReclassificationTransaction,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';
import { take } from 'rxjs/operators';

export interface PreviewRow {
    sourceLabel: string;
    targetLabel: string;
    cyAmount: number | null;
    pyAmount: number | null;
    description: string;
}

export interface PreviewSection {
    statementLabel: string;
    rows: PreviewRow[];
}

@Component({
    selector: 'aumico-reclassification-preview',
    templateUrl: './reclassification-preview.component.html',
    styleUrls: ['./reclassification-preview.component.scss'],
})
export class ReclassificationPreviewComponent implements OnChanges {
    @Input() transactions: IReclassificationTransaction[] = [];
    @Input() currency = 'CHF';
    @Input() locale = 'de-CH';
    @Output() closed = new EventEmitter<void>();

    sections: PreviewSection[] = [];

    title = '';
    closeLabel = '';
    exportPdfLabel = '';
    colFrom = '';
    colTo = '';
    colCY = '';
    colPY = '';
    colDescription = '';
    emptyLabel = '';

    constructor(
        private readonly i18n: I18NextService,
        private readonly annualReportService: AumicoAnnualReportService,
    ) { }

    ngOnChanges(_changes: SimpleChanges): void {
        const t = (k: string, opts?: object): string =>
            this.i18n.t(k, { lng: this.locale, ...(opts ?? {}) }) as string;
        this.title = t('aumico_grouping_masking_reclass_preview_title');
        this.closeLabel = t('aumico_grouping_masking_reclass_preview_close');
        this.exportPdfLabel = t('aumico_grouping_masking_reclass_preview_export_pdf');
        this.colFrom = t('aumico_grouping_masking_reclass_preview_col_from');
        this.colTo = t('aumico_grouping_masking_reclass_preview_col_to');
        this.colCY = t('aumico_grouping_masking_reclass_preview_col_cy', { currency: this.currency });
        this.colPY = t('aumico_grouping_masking_reclass_preview_col_py', { currency: this.currency });
        this.colDescription = t('aumico_grouping_masking_reclass_preview_col_description');
        this.emptyLabel = t('aumico_grouping_masking_reclass_preview_empty');
        this.buildSections();
    }

    private buildSections(): void {
        const order = [
            { key: ReclassificationStatement.BALANCE_SHEET, labelKey: 'aumico_grouping_masking_reclass_preview_section_bs' },
            { key: ReclassificationStatement.INCOME_STATEMENT, labelKey: 'aumico_grouping_masking_reclass_preview_section_is' },
        ];

        this.sections = [];
        for (const { key, labelKey } of order) {
            const txns = this.transactions.filter((t) => t.statement === key);
            if (txns.length === 0) continue;

            const rowMap = new Map<string, PreviewRow>();
            for (const t of txns) {
                const mapKey = `${t.source.accountNumber}|${t.source.name}||${t.target.accountNumber}|${t.target.name}`;
                let row = rowMap.get(mapKey);
                if (!row) {
                    row = {
                        sourceLabel: `${t.source.accountNumber} ${t.source.name}`,
                        targetLabel: `${t.target.accountNumber} ${t.target.name}`,
                        cyAmount: null,
                        pyAmount: null,
                        description: t.description ?? '',
                    };
                    rowMap.set(mapKey, row);
                }
                if (t.period === ReclassificationPeriod.CURRENT_YEAR) {
                    row.cyAmount = (row.cyAmount ?? 0) + t.amount;
                } else {
                    row.pyAmount = (row.pyAmount ?? 0) + t.amount;
                }
                if (t.period === ReclassificationPeriod.CURRENT_YEAR && t.description) {
                    row.description = t.description;
                } else if (!row.description && t.description) {
                    row.description = t.description;
                }
            }

            // Sort rows by source account number (numeric-aware: the source
            // label is prefixed with the account number, so a numeric collation
            // orders "9" before "10" correctly).
            const rows = Array.from(rowMap.values()).sort((a, b) =>
                a.sourceLabel.localeCompare(b.sourceLabel, undefined, { numeric: true, sensitivity: 'base' }),
            );

            this.sections.push({
                statementLabel: this.i18n.t(labelKey, { lng: this.locale }) as string,
                rows,
            });
        }
    }

    formatAmt(n: number | null): string {
        if (n == null) return '–';

        const self = this as this & { _amtFmt?: Intl.NumberFormat; _amtFmtLocale?: string };
        if (!self._amtFmt || self._amtFmtLocale !== this.locale) {
            self._amtFmtLocale = this.locale;
            self._amtFmt = new Intl.NumberFormat(this.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        return self._amtFmt.format(n);
    }

    onClose(): void {
        this.closed.emit();
    }

    onExportPdf(): void {
        const html = this.buildExportHtml();
        this.annualReportService
            .exportHtmlToPdf$(html)
            .pipe(take(1))
            .subscribe({
                next: (pdf: Blob) => this.triggerPdfDownload(pdf),
                error: () => {
                    console.error('Reclassifications PDF export failed.');
                },
            });
    }

    private buildExportHtml(): string {
        const fmt = (n: number): string =>
            new Intl.NumberFormat(this.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

        const sectionRows = this.sections
            .map(
                (s) =>
                    '<tr class="section-row"><td colspan="5">' + s.statementLabel + '</td></tr>' +
                    s.rows
                        .map(
                            (r) =>
                                '<tr>' +
                                '<td>' + r.sourceLabel + '</td>' +
                                '<td>' + r.targetLabel + '</td>' +
                                '<td class="right">' + (r.cyAmount != null ? fmt(r.cyAmount) : '–') + '</td>' +
                                '<td class="right">' + (r.pyAmount != null ? fmt(r.pyAmount) : '–') + '</td>' +
                                '<td>' + r.description + '</td>' +
                                '</tr>',
                        )
                        .join(''),
            )
            .join('');

        const screenStyles = [
            '* { box-sizing: border-box; }',
            'body { font-family: Arial, sans-serif; font-size: 12px; color: #111; margin: 0; background: #f4f5f7; }',
            'h1 { margin: 0 0 20px; font-size: 18px; font-weight: 700; color: #126978; }',
            'table { width: 100%; border-collapse: collapse; }',
            'thead tr { background: #126978; }',
            'thead th { padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 700;' +
            ' text-transform: uppercase; letter-spacing: .5px; color: #fff; }',
            'thead th.right { text-align: right; }',
            'tbody td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; vertical-align: middle; }',
            'td.right { text-align: right; font-variant-numeric: tabular-nums; }',
            'tr.section-row td { background: #f0fafa; font-weight: 700; color: #126978; font-size: 12px; padding: 7px 12px; }',
            '.note { margin-top: 20px; font-size: 11px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 12px; }',
            '.container { max-width: 960px; margin: 32px auto; background: #fff;' +
            ' border-radius: 8px; box-shadow: 0 4px 24px rgba(0,0,0,.1); padding: 28px; }',
        ].join(' ');

        // `@page` controls PDF page size and margins for Gotenberg/Chromium.
        // `print-color-adjust: exact` prevents Chromium from stripping background
        // colors from the table header and section rows during PDF rendering.
        const printStyles = [
            '@page { margin: 1.2cm; size: A4 landscape; }',
            '@media print {',
            '  body { background: #fff; margin: 0; width: 100%; }',
            '  .container { box-shadow: none; border-radius: 0; margin: 0; padding: 0; max-width: 100%; width: 100%; }',
            '  table { width: 100%; }',
            '  thead tr { background: #126978 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
            '  thead th { color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
            '  tr.section-row td { background: #f0fafa !important; color: #126978 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
            '  table { page-break-inside: auto; }',
            '  tr { page-break-inside: avoid; }',
            '}',
        ].join(' ');

        return (
            '<!DOCTYPE html><html><head>' +
            '<meta charset="utf-8">' +
            '<title>' + this.title + '</title>' +
            '<style>' + screenStyles + ' ' + printStyles + '</style>' +
            '</head>' +
            '<body>' +
            '<div class="container">' +
            '<h1>' + this.title + '</h1>' +
            '<table>' +
            '<thead><tr>' +
            '<th>' + this.colFrom + '</th>' +
            '<th>' + this.colTo + '</th>' +
            '<th class="right">' + this.colCY + '</th>' +
            '<th class="right">' + this.colPY + '</th>' +
            '<th>' + this.colDescription + '</th>' +
            '</tr></thead>' +
            '<tbody>' + sectionRows + '</tbody>' +
            '</table>' +
            '</div>' +
            '</body></html>'
        );
    }

    private triggerPdfDownload(pdf: Blob): void {
        const filename = this.buildPdfFilename();
        const blobUrl = URL.createObjectURL(pdf);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }

    private buildPdfFilename(): string {
        const date = new Date().toISOString().slice(0, 10);
        const safeTitle =
            (this.title || 'reclassifications').replace(/[\\/:*?"<>|]+/g, '').trim() || 'reclassifications';
        return `${safeTitle}_${date}.pdf`;
    }
}
